import { useEffect, useRef } from "react";
import { api } from "@/lib/api";

/**
 * AICoHostPlayer — silently subscribes to the stream's WebSocket and auto-plays
 * any incoming `ai_cohost` audio chunks. No visible UI; just an Audio element
 * that gets new sources fed into it.
 *
 * Mounted once per LiveStream page. Reads from the existing wsRef so it doesn't
 * open a second WebSocket.
 */
export default function AICoHostPlayer({ wsRef, onLine }) {
  const audioRef = useRef(null);
  const queueRef = useRef([]); // pending base64 audio chunks
  const playingRef = useRef(false);

  // Browser autoplay policy: programmatic <audio>.play() is blocked
  // until a user gesture has unlocked it. AICoHostPanel dispatches a
  // `bidoria:unlock-audio` event when the user toggles Bidoria ON or
  // taps Hype Now — both confirmed user gestures. We synchronously
  // play() a silent buffer on OUR <audio> element inside that event
  // handler so subsequent programmatic plays succeed.
  useEffect(() => {
    const handler = () => {
      const a = audioRef.current;
      if (!a) return;
      try {
        const prevSrc = a.src;
        const prevVol = a.volume;
        a.volume = 0;
        a.src =
          "data:audio/mpeg;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAA";
        const p = a.play();
        if (p && typeof p.catch === "function") {
          p.catch(() => { /* noop */ });
        }
        // Restore so the queue's .play() picks up cleanly.
        setTimeout(() => {
          try {
            a.volume = prevVol;
            if (prevSrc) a.src = prevSrc;
          } catch { /* noop */ }
        }, 100);
      } catch { /* noop */ }
    };
    window.addEventListener("bidoria:unlock-audio", handler);
    return () => window.removeEventListener("bidoria:unlock-audio", handler);
  }, []);

  // Wire into the parent's WebSocket. The parent's onmessage handler still owns
  // the bulk of routing — we just register a side channel on the same socket
  // by listening to the raw "message" event in addition.
  useEffect(() => {
    const ws = wsRef?.current;
    if (!ws) return;

    const handler = (evt) => {
      try {
        const data = JSON.parse(evt.data);
        if (data.type !== "ai_cohost") return;
        if (data.audio_b64) {
          queueRef.current.push({
            src: `data:${data.mime || "audio/mpeg"};base64,${data.audio_b64}`,
            text: data.text,
            personality: data.personality,
            ts: data.ts,
          });
          drain();
        } else if (data.text && onLine) {
          // Text-only fallback when voice synthesis failed.
          onLine({ text: data.text, personality: data.personality, ts: data.ts });
        }
      } catch {
        /* not JSON or unrelated */
      }
    };

    ws.addEventListener("message", handler);
    return () => ws.removeEventListener("message", handler);
    // eslint-disable-next-line
  }, [wsRef?.current]);

  const drain = () => {
    if (playingRef.current) return;
    const next = queueRef.current.shift();
    if (!next) return;
    const a = audioRef.current;
    if (!a) return;
    playingRef.current = true;
    if (onLine) {
      onLine({ text: next.text, personality: next.personality, ts: next.ts });
    }
    a.src = next.src;
    a.volume = 0.85;
    a.play().catch(() => {
      // Autoplay may be blocked until the user interacts with the page.
      // We log and drop this chunk; future chunks will play once the user
      // clicks anywhere on the page (browser unlocks audio context).
      playingRef.current = false;
    });
  };

  const onEnded = () => {
    playingRef.current = false;
    drain();
  };

  return (
    <audio
      ref={audioRef}
      onEnded={onEnded}
      onError={onEnded}
      preload="auto"
      data-testid="ai-cohost-audio-player"
      style={{ display: "none" }}
    />
  );
}

// Simple helper if a callsite wants to hit one of the controls programmatically.
export async function hypeNow(streamId) {
  return api.post(`/streams/${streamId}/ai-cohost/hype`, {});
}
